// @flow

import { application as APP_CONFIG } from 'Config';
import { artist as ENUM_ARTIST } from 'Enum';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { LocationManager } from 'shared_services/riseart/url/Location';
import { generateHrefLangParams } from 'shared_services/riseart/meta/utils';

export const MENU_ITEMS = {
  profile: 'profile',
  cv: 'cv',
  studio: 'studio',
  news: 'news',
};

/**
 * getArtistMenuItems
 *
 * @param {Object} data
 * @param {Function} formatMessage
 * @param {string} selected
 * @param {Object} locale
 * @returns {Array<Object>}
 */
export const getArtistMenuItems = (
  data: Object,
  formatMessage: Function,
  selected: string,
  locale: Object,
): Array<Object> => {
  const urlParams = [data.id, data.alias, { locale }];
  const menuItems = [
    {
      key: MENU_ITEMS.profile,
      label: formatMessage({ id: `components.artist.${MENU_ITEMS.profile}` }),
      url: MENU_ITEMS.profile === selected ? null : UrlAssembler.artistProfile(...urlParams),
      selected: MENU_ITEMS.profile === selected,
    },
  ];

  // CV item
  if (
    data.hasDegrees ||
    data.hasAwards ||
    data.hasExhibitions ||
    data.hasCollections ||
    data.hasBibliography
  ) {
    menuItems.push({
      key: MENU_ITEMS.cv,
      label: formatMessage({ id: `components.artist.${MENU_ITEMS.cv}` }),
      url: UrlAssembler.artistSubpage('artistCv', ...urlParams),
      selected: MENU_ITEMS.cv === selected,
    });
  }

  // Studio item
  if (data.hasFeed) {
    menuItems.push({
      key: MENU_ITEMS.studio,
      label: formatMessage({ id: `components.artist.${MENU_ITEMS.studio}` }),
      url: UrlAssembler.artistSubpage('artistStudio', ...urlParams),
      selected: MENU_ITEMS.studio === selected,
    });
  }

  // News item
  if (data.hasEvents || data.hasArticles) {
    menuItems.push({
      key: MENU_ITEMS.news,
      label: formatMessage({ id: `components.artist.${MENU_ITEMS.news}` }),
      url: UrlAssembler.artistSubpage('artistNews', ...urlParams),
      selected: MENU_ITEMS.news === selected,
    });
  }

  return menuItems;
};

/**
 * getArtistImages
 *
 * @param {Array<Object>} artistImages
 * @returns {{ mainBannerImage: Object, additionalBannerImage: Object, detailsImage: Object }} Images by types
 */
export const getArtistImagesByType = (
  artistImages: Array<Object>,
): { mainBannerImage: Object, additionalBannerImage: Object, detailsImage: Object } => {
  if (!artistImages) {
    return {};
  }

  const imagesByType = artistImages.reduce((accumulator, image) => {
    if (image.type) {
      accumulator[image.type] = image;
    }

    return accumulator;
  }, {});

  return {
    mainBannerImage: imagesByType[ENUM_ARTIST.image.type.TYPE_BANNER_MASTER],
    additionalBannerImage: imagesByType[ENUM_ARTIST.image.type.TYPE_ADDITIONAL_MASTER],
    detailsImage: imagesByType[ENUM_ARTIST.image.type.TYPE_DETAIL_MASTER],
  };
};

/**
 * mapArtistPageData
 *
 * @param {?Object} artistData
 * @param {?Object} error
 * @param {string} selectedMenuItem
 * @param {?string} subpageKey
 * @param {Function} formatMessage
 * @param {Object} locale
 * @param {?string} subpageKey
 * @returns {Object}
 */
export const mapArtistPageData = (
  artistData: ?Object,
  error: ?Object,
  selectedMenuItem: string,
  subpageKey: string,
  {
    formatMessage,
    locale,
    location,
  }: { formatMessage: Function, locale: Object, location: Object },
): Object => ({
  hasError: !!error || !artistData,
  canonicalURL: artistData
    ? UrlAssembler.artistProfile(artistData.id, artistData.alias, {
        locale,
        origin: LocationManager.get('origin'),
      })
    : LocationManager.get('originWithPath'),
  ...(subpageKey
    ? {
        subpageCanonicalURL: artistData
          ? UrlAssembler.artistSubpage(subpageKey, artistData.id, artistData.alias, {
              locale,
              origin: LocationManager.get('origin'),
            })
          : LocationManager.get('originWithPath'),
        subpageCanonicalRedirect: artistData
          ? UrlAssembler.artistSubpage(subpageKey, artistData.id, artistData.alias, {
              locale,
              ...(location.search ? { search: location.search } : null),
              ...(location.hash ? { hash: location.hash } : null),
            })
          : LocationManager.get('originWithPath'),
      }
    : null),
  hrefLangRedirects: artistData
    ? generateHrefLangParams(
        APP_CONFIG.i18n.locales.map(({ name }) => ({
          localeCode: name,
          id: artistData.id,
          alias: artistData.alias,
        })),
      )
    : null,
  name: artistData && artistData.name,
  artistUrl: artistData && UrlAssembler.artistProfile(artistData.id, artistData.alias, { locale }),
  artistMenuItems: artistData
    ? getArtistMenuItems(artistData, formatMessage, selectedMenuItem, locale)
    : [],
  artistImages: getArtistImagesByType(artistData && artistData.images),
});
